/* CreditsExhaustedGate — the out-of-credits dialog for signed-in users.
 *
 * Mounted once in AppShell. services/api.js turns a 429 CREDITS_EXHAUSTED into a
 * window event and this listens for it, so no AI page needs to know the code
 * (same pattern as GuestSignupGate for guests).
 *
 * Web: offer the upgrade first (Pro -> Elite), then a top-up. Elite users have
 * nothing above them, so they see top-ups only.
 * iOS app: plain text only, "Manage your plan and credits on play-jai.com", with
 * NO link or button to a purchase (linking out may break Apple's rules). The
 * server also leaves the offer out of the response for the app.
 *
 * No emoji (UI chrome).
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { CREDITS_EXHAUSTED_EVENT } from '../services/api'
import { IS_NATIVE_APP } from '../services/runtime'
import { PLANS } from '../config/plans'
import { trackEvent } from '../analytics'
import MascotDialog from './draft/MascotDialog'
import TopupPacks from './TopupPacks'
import { SolidButton } from './ModalPortal'

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'long' }) } catch { return '' }
}

export default function CreditsExhaustedGate() {
  const { isGuest } = useAuth()
  const navigate = useNavigate()
  const [info, setInfo] = useState(null)

  useEffect(() => {
    const onExhausted = (event) => {
      // The backend only sends this code to signed-in users; a guest has the
      // sign-up wall instead and must never see a purchase dialog.
      if (isGuest) return
      setInfo((prev) => {
        if (!prev) trackEvent('credits_exhausted', { plan: event.detail?.currentPlan || '', page: window.location.pathname })
        return event.detail || {}
      })
    }
    window.addEventListener(CREDITS_EXHAUSTED_EVENT, onExhausted)
    return () => window.removeEventListener(CREDITS_EXHAUSTED_EVENT, onExhausted)
  }, [isGuest])

  if (!info || isGuest) return null

  const close = () => setInfo(null)
  const reset = fmtDate(info.resetAt)
  const upgradeName = info.upgradeTo ? PLANS.find((p) => p.id === info.upgradeTo)?.name : null

  if (IS_NATIVE_APP) {
    return (
      <MascotDialog
        state="focused"
        size={120}
        headline="You've used your JAI credits"
        body={`${reset ? `Your allowance resets on ${reset}. ` : ''}Manage your plan and credits on play-jai.com.`}
        onClose={close}
        actions={[{ label: 'OK', primary: true, onClick: close }]}
      />
    )
  }

  const goUpgrade = () => { trackEvent('credits_gate_cta', { target: 'upgrade' }); close(); navigate('/pricing') }

  return (
    <MascotDialog
      state="focused"
      size={120}
      width={540}
      headline="You've used your JAI credits"
      body={`${reset ? `Your allowance resets on ${reset}. ` : ''}${upgradeName
        ? `${upgradeName} includes more credits every month, or you can top up now.`
        : 'You can top up now and keep going.'}`}
      onClose={close}
      actions={[{ label: 'Not now', onClick: close }]}
    >
      {/* Upgrade first, then the top-ups. Elite has nothing above it, so it
          sees the top-ups only. */}
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {upgradeName && (
          <div><SolidButton onClick={goUpgrade}>{`Upgrade to ${upgradeName}`}</SolidButton></div>
        )}
        <TopupPacks />
      </div>
    </MascotDialog>
  )
}
